import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth, useCan } from '../contexts/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
    ArrowLeft, Pencil, Phone, Mail, User, MapPin,
    Calendar as CalendarIcon, Building, FileText, Target,
    Truck, Check, Clock, Trash2, Layers, ChevronUp, ChevronDown, Save, History, ClipboardList, Eye
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
    { value: 'visite_presentiel', label: 'Visite présentiel', color: 'bg-[#1B365D]' },
    { value: 'visite_virtuelle', label: 'Visite virtuelle', color: 'bg-[#C9A962]' },
];

const VISIT_STATUSES = [
    { value: 'planifiee', label: 'Planifiée', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    { value: 'commencee', label: 'Commencer visite', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    { value: 'terminee', label: 'Terminée', color: 'bg-green-100 text-green-700 border-green-200' },
    { value: 'annulee', label: 'Annulée', color: 'bg-red-100 text-red-700 border-red-200' },
    { value: 'reportee', label: 'Reportée', color: 'bg-orange-100 text-orange-700 border-orange-200' },
];

const SOURCES = [
    { value: 'appel_simple', label: 'Appel simple' },
    { value: 'appel_google', label: 'Appel Google' },
    { value: 'formulaire_site', label: 'Formulaire site' },
    { value: 'message_google', label: 'Message Google' },
    { value: 'recommandation', label: 'Recommandation' },
    { value: 'ancien_client', label: 'Ancien client' },
    { value: 'plateforme_externe', label: 'Plateforme externe' },
];

const DEPARTEMENTS = [
    { value: '75', label: '75 - Paris' },
    { value: '77', label: '77 - Seine-et-Marne' },
    { value: '78', label: '78 - Yvelines' },
    { value: '91', label: '91 - Essonne' },
    { value: '92', label: '92 - Hauts-de-Seine' },
    { value: '93', label: '93 - Seine-Saint-Denis' },
    { value: '94', label: '94 - Val-de-Marne' },
    { value: '95', label: "95 - Val-d'Oise" },
    { value: 'hors_idf', label: 'Hors Île-de-France' },
];

const ASCENSEURS = [
    { value: 'non', label: 'Non' },
    { value: '1_3', label: '1 à 3 personnes' },
    { value: '4_6', label: '4 à 6 personnes' },
    { value: '7_9', label: '7 à 9 personnes' },
    { value: '10_plus', label: '10 personnes et plus' },
    { value: 'indetermine', label: 'Indéterminé' },
    { value: 'interdit', label: 'Interdit' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
    const h = i.toString().padStart(2, '0');
    return [{ value: `${h}:00`, label: `${h}:00` }, { value: `${h}:30`, label: `${h}:30` }];
}).flat();

const CAN_ASSIGN = ['admin', 'assistante', 'responsable'];
const getTypeConfig = (t) => EVENT_TYPES.find(x => x.value === t) || EVENT_TYPES[0];
const getVisitStatusConfig = (s) => VISIT_STATUSES.find(x => x.value === s) || VISIT_STATUSES[0];
const getSourceLabel = (s) => SOURCES.find(x => x.value === s)?.label || s || '-';
const getDeptLabel = (d) => DEPARTEMENTS.find(x => x.value === d)?.label || d || '-';
const getAscLabel = (a) => ASCENSEURS.find(x => x.value === a)?.label || a || '-';

/* ============ Accordion Section (same style as ProspectForm) ============ */
function AccordionSection({ title, isOpen, onToggle, children, completed, highlight }) {
    return (
        <div className={`bg-white rounded-lg shadow-sm overflow-hidden border ${highlight ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-100'}`}>
            <Collapsible open={isOpen} onOpenChange={onToggle}>
                <CollapsibleTrigger className="w-full">
                    <div className="flex items-center justify-between p-5 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <div className={`w-1.5 h-8 rounded-full transition-colors ${highlight ? 'bg-red-500' : (completed ? 'bg-green-500' : 'bg-[#C9A962]')}`} />
                            <h3 className={`text-lg font-semibold ${highlight ? 'text-red-700' : 'text-[#1B365D]'}`}>{title}</h3>
                            {highlight ? (
                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Incomplet</span>
                            ) : (completed && <Check className="w-5 h-5 text-green-500" />)}
                        </div>
                        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="border-t border-gray-100">
                        <div className={`border-l-4 ml-5 transition-colors ${completed ? 'border-green-500' : 'border-[#C9A962]'}`}>
                            {children}
                        </div>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
}

/* ============ Info Row (read-only display) ============ */
function InfoRow({ label, value, icon: Icon, href }) {
    const content = href
        ? <a href={href} className="text-[#1B365D] font-medium hover:underline">{value || '-'}</a>
        : <span className="text-[#1B365D] font-medium">{value || '-'}</span>;
    return (
        <div className="flex flex-col sm:flex-row sm:items-center py-4 px-6 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center gap-3 sm:w-1/3 mb-1 sm:mb-0">
                {Icon && <Icon className="w-4 h-4 text-[#1B365D]/50" />}
                <span className="text-gray-500 text-sm">{label}</span>
            </div>
            <div className="sm:w-2/3">{content}</div>
        </div>
    );
}

/* ============ Form Row (edit mode, same style as ProspectForm) ============ */
function FormRow({ label, icon: Icon, children }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center py-5 px-6 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center gap-3 sm:w-1/3 mb-2 sm:mb-0">
                {Icon && <Icon className="w-5 h-5 text-[#1B365D]/50" />}
                <span className="text-[#1B365D] font-medium">{label}</span>
            </div>
            <div className="sm:w-2/3">{children}</div>
        </div>
    );
}

export default function EventDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const { canWrite: canWriteAgenda, canDelete: canDeleteAgenda } = useCan('agenda');
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editForm, setEditForm] = useState({});

    // Accordion states for detail view (all open by default)
    const [openSections, setOpenSections] = useState({
        rdv: true, source: true, contact: true, depart: true, arrivee: true, remarques: true
    });
    // Accordion states for edit form
    const [editSections, setEditSections] = useState({
        rdv: true, assignment: true
    });

    // ============ "Visite terminée" validation ============
    const sectionRefs = useRef({});
    const [incompleteDialog, setIncompleteDialog] = useState({ open: false, sections: [] });
    const [inventaireItemsCount, setInventaireItemsCount] = useState(null);
    // Snapshot of incomplete section IDs for UI highlighting
    const [incompleteSectionIds, setIncompleteSectionIds] = useState([]);

    const canAssign = CAN_ASSIGN.includes(user?.role);
    const { hasChanges, setHasChanges, UnsavedDialog, confirmNavigation } = useUnsavedChanges();

    // ============ Cancel-reason dialog (visit status -> annulee) ============
    const [cancelDialog, setCancelDialog] = useState({ open: false, reason: '', submitting: false });

    const fetchEvent = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/events/${id}`, { withCredentials: true });
            setEvent(res.data);
        } catch {
            toast.error('Erreur lors du chargement');
            navigate('/agenda');
        } finally { setLoading(false); }
    }, [id, navigate]);

    useEffect(() => { fetchEvent(); }, [fetchEvent]);
    useEffect(() => {
        if (canAssign) {
            axios.get(`${API_URL}/api/users/list`, { withCredentials: true })
                .then(r => setUsers(r.data)).catch(() => {});
        }
    }, [canAssign]);
    useEffect(() => {
        axios.get(`${API_URL}/api/prospects`, { withCredentials: true })
            .then(r => setProspects(r.data)).catch(() => {});
    }, []);

    // Fetch inventory items count whenever the linked prospect changes
    useEffect(() => {
        const pid = event?.prospect_id;
        if (!pid) { setInventaireItemsCount(null); return; }
        axios.get(`${API_URL}/api/prospects/${pid}/event-inventaire`, { withCredentials: true })
            .then(r => setInventaireItemsCount((r.data?.items || []).length))
            .catch(() => setInventaireItemsCount(0));
    }, [event?.prospect_id]);

    // Build the list of incomplete sections (used for "Visite terminée" validation)
    // Pure function: accepts fresh data explicitly to avoid stale closures.
    const computeIncompleteFromData = (evt, invCount) => {
        const p = evt?.prospect || {};
        const missing = [];

        // Helpers to detect "filled" values (treat "0" as filled, "" / null / undefined as empty)
        const hasVal = (v) => v !== undefined && v !== null && String(v).trim() !== '';

        // Source
        const sourceFields = [];
        if (!hasVal(p.source)) sourceFields.push('Source du prospect');
        if (!hasVal(p.departement)) sourceFields.push('Département');
        if (sourceFields.length) missing.push({ id: 'source', title: 'Source', fields: sourceFields });

        // Contact
        const contactFields = [];
        if (!hasVal(p.nom)) contactFields.push('Nom');
        if (!hasVal(p.telephone)) contactFields.push('Téléphone');
        if (!hasVal(p.email)) contactFields.push('Email');
        if (contactFields.length) missing.push({ id: 'contact', title: 'Contact', fields: contactFields });

        // Adresse de départ
        const ad = p.adresse_depart || {};
        const depFields = [];
        if (!hasVal(ad.date)) depFields.push('Date chargement');
        if (!hasVal(ad.adresse)) depFields.push('Adresse');
        if (!hasVal(ad.etage)) depFields.push('Étage');
        if (!hasVal(ad.ascenseur)) depFields.push('Ascenseur');
        if (!hasVal(ad.portage)) depFields.push('Portage');
        if (ad.autorisation_stationnement === undefined || ad.autorisation_stationnement === null) depFields.push('Autorisation stationnement');
        if (ad.monte_meuble === undefined || ad.monte_meuble === null) depFields.push('Monte-meuble');
        if (depFields.length) missing.push({ id: 'depart', title: 'Adresse de départ', fields: depFields });

        // Adresse d'arrivée
        const aa = p.adresse_arrivee || {};
        const arrFields = [];
        if (!hasVal(aa.date)) arrFields.push('Date déchargement');
        if (!hasVal(aa.adresse)) arrFields.push('Adresse');
        if (!hasVal(aa.etage)) arrFields.push('Étage');
        if (!hasVal(aa.ascenseur)) arrFields.push('Ascenseur');
        if (!hasVal(aa.portage)) arrFields.push('Portage');
        if (aa.autorisation_stationnement === undefined || aa.autorisation_stationnement === null) arrFields.push('Autorisation stationnement');
        if (aa.monte_meuble === undefined || aa.monte_meuble === null) arrFields.push('Monte-meuble');
        if (arrFields.length) missing.push({ id: 'arrivee', title: "Adresse d'arrivée", fields: arrFields });

        // Formule
        if (!p.formules || p.formules.length === 0) {
            missing.push({ id: 'formule', title: 'Formule', fields: ['Au moins une formule'] });
        }

        // Garde meuble
        const gmFields = [];
        if (!hasVal(p.garde_meuble)) gmFields.push('Garde meuble (oui/non)');
        else if (p.garde_meuble === 'oui') {
            if (!hasVal(p.garde_meuble_duree)) gmFields.push('Durée');
            if (!hasVal(p.garde_meuble_mensualite)) gmFields.push('Mensualité');
        }
        if (gmFields.length) missing.push({ id: 'garde_meuble', title: 'Garde meuble', fields: gmFields });

        // Calcul tarif (tous les champs — stockés dans simulateur_devis)
        const simEvt = p.simulateur_devis || {};
        const ctFields = [];
        if (!hasVal(simEvt.volume_devis)) ctFields.push('Volume devis');
        if (!hasVal(simEvt.camion_20m3) && !hasVal(simEvt.camion_30m3)) ctFields.push('Camion 20 m³ ou 30 m³');
        if (!hasVal(simEvt.distance)) ctFields.push('Distance (km)');
        if (!hasVal(ad.date)) ctFields.push('Date chargement');
        if (!hasVal(aa.date)) ctFields.push('Date déchargement');
        if (ctFields.length) missing.push({ id: 'calcul_tarif', title: 'Calcul tarif', fields: ctFields });

        // Inventaire (items)
        if (invCount !== null && invCount === 0) {
            missing.push({ id: 'inventaire', title: 'Inventaire / Volume', fields: ["Aucun article dans l'inventaire"] });
        }

        // Fournitures (dans simulateur_devis)
        const sim = p.simulateur_devis || {};
        if (sim.fournitures_active === true) {
            const fournKeys = ['cartons_standards', 'cartons_livres', 'adhesif', 'papier_bulle', 'autres'];
            const atLeastOne = fournKeys.some(k => sim[k] && parseFloat(sim[k]) > 0);
            if (!atLeastOne) {
                missing.push({ id: 'fournitures', title: 'Fournitures', fields: ['Au moins une quantité (cartons, adhésif, papier bulle...)'] });
            }
        }

        // Calcul des coûts (nombre_jours par formule)
        if (p.formules && p.formules.length > 0) {
            const cc = sim.calcul_couts || {};
            const nj = cc.nombre_jours || {};
            const missingFormulesJours = p.formules.filter(f => !nj[f] || parseInt(nj[f]) <= 0);
            if (missingFormulesJours.length > 0) {
                const labels = { economique: 'Économique', economique_plus: 'Économique plus', standard: 'Standard', luxe: 'Luxe' };
                missing.push({
                    id: 'calcul_couts',
                    title: 'Calcul des coûts',
                    fields: missingFormulesJours.map(f => `Nombre de jours — ${labels[f] || f}`),
                });
            }
        }

        return missing;
    };

    const getIncompleteSections = useCallback(() => {
        return computeIncompleteFromData(event, inventaireItemsCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event, inventaireItemsCount]);

    const handleVisitStatusChange = async (newStatus) => {
        // Intercept "annulee" to prompt for a reason that will persist on event + prospect
        if (newStatus === 'annulee') {
            setCancelDialog({ open: true, reason: '', submitting: false });
            return;
        }
        try {
            await axios.put(`${API_URL}/api/events/${id}`, { visit_status: newStatus }, { withCredentials: true });
            setEvent(prev => ({ ...prev, visit_status: newStatus }));
            setIncompleteSectionIds([]);
            toast.success('État de la visite mis à jour');
            if (newStatus === 'commencee' && event.prospect_id) {
                try {
                    await axios.post(`${API_URL}/api/events/${id}/auto-inventaire`, {}, { withCredentials: true });
                } catch { /* inventory may already exist */ }
            }
        } catch { toast.error('Erreur'); }
    };

    const submitCancellation = async () => {
        const reason = cancelDialog.reason.trim();
        if (!reason) { toast.error("Veuillez saisir la raison de l'annulation"); return; }
        setCancelDialog(d => ({ ...d, submitting: true }));
        try {
            await axios.put(`${API_URL}/api/events/${id}`, {
                visit_status: 'annulee',
                cancel_reason: reason,
            }, { withCredentials: true });
            setEvent(prev => ({
                ...prev,
                visit_status: 'annulee',
                cancel_reason: reason,
                cancelled_at: new Date().toISOString(),
                cancelled_by: user?.name || user?.email,
            }));
            setIncompleteSectionIds([]);
            setCancelDialog({ open: false, reason: '', submitting: false });
            toast.success('Visite annulée');
        } catch {
            toast.error('Erreur');
            setCancelDialog(d => ({ ...d, submitting: false }));
        }
    };

    const scrollToFirstIncomplete = () => {
        const first = incompleteDialog.sections[0];
        setIncompleteDialog({ open: false, sections: [] });
        if (!first) return;
        setOpenSections(prev => ({ ...prev, [first.id]: true }));
        setTimeout(() => {
            const el = sectionRefs.current[first.id];
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
    };

    const goEditProspect = () => {
        if (!event?.prospect_id) return;
        setIncompleteDialog({ open: false, sections: [] });
        navigate(`/prospects/${event.prospect_id}/modifier?context=extended&from=agenda&event_id=${id}`);
    };

    const openEdit = () => {
        setEditForm({
            title: event.title, type: event.type, date: event.date,
            start_time: event.start_time || '09:00', end_time: event.end_time || '10:00',
            location: event.location || '', description: event.description || '',
            assigned_to: event.assigned_to || '', prospect_id: event.prospect_id || '',
        });
        setEditSections({ rdv: true, assignment: true });
        setShowEditModal(true);
        setHasChanges(false);
    };

    const autoEndTime = (start) => {
        const [h, m] = start.split(':').map(Number);
        const endH = Math.min(h + 1, 23);
        return `${endH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    const updateEditForm = (updates) => {
        if (updates.start_time) {
            updates.end_time = autoEndTime(updates.start_time);
        }
        setEditForm(prev => ({ ...prev, ...updates }));
        setHasChanges(true);
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        if (!editForm.title.trim()) { toast.error('Le titre est requis'); return; }
        setSubmitting(true);
        try {
            const payload = { ...editForm };
            if (!payload.assigned_to) delete payload.assigned_to;
            if (!payload.prospect_id) delete payload.prospect_id;
            await axios.put(`${API_URL}/api/events/${id}`, payload, { withCredentials: true });
            toast.success('Événement modifié');
            setShowEditModal(false);
            setHasChanges(false);
            fetchEvent();
        } catch (err) {
            // Surface the real backend error so users (and support) understand
            // why an event update failed (permission denied, validation, etc.).
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === 'string' && detail
                ? detail
                : err?.response?.status === 403
                    ? "Accès refusé : vous devez être assigné à ce RDV pour le modifier."
                    : 'Erreur lors de la modification';
            toast.error(msg);
        }
        finally { setSubmitting(false); }
    };

    const handleDelete = async () => {
        setSubmitting(true);
        try {
            await axios.delete(`${API_URL}/api/events/${id}`, { withCredentials: true });
            toast.success('Événement supprimé');
            setHasChanges(false);
            navigate('/agenda');
        } catch { toast.error('Erreur'); }
        finally { setSubmitting(false); }
    };

    const handleBack = () => confirmNavigation(() => navigate('/agenda'));

    const fmtDate = (d) => {
        if (!d) return '-';
        try { return format(parseISO(d), 'EEEE d MMMM yyyy', { locale: fr }); } catch { return d; }
    };
    const fmtDateShort = (d) => {
        if (!d) return '-';
        try { return format(new Date(d), 'dd MMMM yyyy', { locale: fr }); } catch { return d; }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center h-64">
                    <div className="w-8 h-8 border-4 border-[#1B365D] border-t-transparent rounded-full animate-spin" />
                </div>
            </Layout>
        );
    }
    if (!event) return null;

    const tc = getTypeConfig(event.type);
    const vs = getVisitStatusConfig(event.visit_status);
    const p = event.prospect;
    const toggle = (key) => setOpenSections(s => ({ ...s, [key]: !s[key] }));
    const visiteActive = ['commencee', 'terminee'].includes(event.visit_status);

    return (
        <Layout>
            <Toaster position="top-right" richColors />
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={handleBack} className="text-[#1B365D] hover:bg-[#1B365D]/10" data-testid="back-button">
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {event.title}
                            </h1>
                            <div className="flex items-center gap-2 mt-1">
                                <Badge className={`${tc.color} text-white`}>{tc.label}</Badge>
                                {event.assigned_to_name && (
                                    <span className="text-gray-500 text-sm flex items-center gap-1">
                                        <User className="w-3.5 h-3.5" /> {event.assigned_to_name}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {['admin', 'assistante', 'responsable'].includes(user?.role) && (
                            <Button onClick={openEdit}
                                disabled={!canWriteAgenda}
                                title={!canWriteAgenda ? 'Accès refusé : permission Écriture requise' : 'Modifier'}
                                className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="edit-event-button">
                                <Pencil className="w-4 h-4" /> <span className="hidden sm:inline">Modifier</span>
                            </Button>
                        )}
                        {visiteActive && event.prospect_id && ['admin', 'commercial', 'responsable'].includes(user?.role) && (
                            <Button onClick={() => navigate(`/prospects/${event.prospect_id}/modifier?context=extended&from=agenda&event_id=${id}`)} className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white gap-2" data-testid="edit-prospect-button">
                                <User className="w-4 h-4" /> <span className="hidden sm:inline">Modifier prospect</span>
                            </Button>
                        )}
                        {event.prospect_id && (
                            <Button onClick={() => navigate(`/prospects/${event.prospect_id}?context=extended&from=agenda&event_id=${id}`)} variant="outline" className="border-[#1B365D]/20 text-[#1B365D] hover:bg-[#1B365D]/5 gap-2" data-testid="view-prospect-button">
                                <Eye className="w-4 h-4" /> <span className="hidden sm:inline">Voir prospect</span>
                            </Button>
                        )}
                        {visiteActive && (
                            <Button onClick={() => navigate(`/agenda/${id}/inventaire`)} className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white gap-2" data-testid="inventory-event-button">
                                <ClipboardList className="w-4 h-4" /> <span className="hidden sm:inline">Inventaire</span>
                            </Button>
                        )}
                        {['admin', 'assistante', 'responsable'].includes(user?.role) && (
                            <Button variant="outline" onClick={() => setShowDeleteConfirm(true)}
                                disabled={!canDeleteAgenda}
                                title={!canDeleteAgenda ? 'Accès refusé : permission Suppression requise' : 'Supprimer'}
                                className="text-red-600 border-red-200 hover:bg-red-50 gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="delete-event-button">
                                <Trash2 className="w-4 h-4" /> <span className="hidden sm:inline">Supprimer</span>
                            </Button>
                        )}
                    </div>
                </div>

                {/* État de la visite */}
                <Card className="mb-6 border-2 border-[#C9A962]/30 bg-gradient-to-r from-[#1B365D]/5 to-[#C9A962]/10">
                    <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Layers className="w-6 h-6 text-[#C9A962]" />
                                <span className="text-lg font-semibold text-[#1B365D]">État de la visite</span>
                            </div>
                            <Select value={event.visit_status || 'planifiee'} onValueChange={handleVisitStatusChange}>
                                <SelectTrigger className={`w-full sm:w-64 ${vs.color} border font-medium`} data-testid="visit-status-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {VISIT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {event.visit_status === 'annulee' && event.cancel_reason && (
                            <div className="mt-4 rounded-lg border border-red-200 bg-red-50/60 px-4 py-3" data-testid="event-cancel-reason">
                                <div className="text-[11px] font-bold uppercase tracking-wide text-red-700 mb-1">Raison de l'annulation</div>
                                <div className="text-sm text-red-900 whitespace-pre-wrap break-words">{event.cancel_reason}</div>
                                {event.cancelled_at && (
                                    <div className="text-[10px] text-red-700/70 mt-1.5">
                                        Annulée le {new Date(event.cancelled_at).toLocaleDateString('fr-FR')} à {new Date(event.cancelled_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                        {event.cancelled_by ? ` par ${event.cancelled_by}` : ''}
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Detail sections - Accordion style */}
                <div className="space-y-4">
                    {/* Rendez-vous */}
                    <AccordionSection title="Rendez-vous" isOpen={openSections.rdv} onToggle={() => toggle('rdv')} completed>
                        <InfoRow label="Date" value={fmtDate(event.date)} icon={CalendarIcon} />
                        <InfoRow label="Heure début" value={event.start_time || '-'} icon={Clock} />
                        <InfoRow label="Heure fin" value={event.end_time || '-'} icon={Clock} />
                        <InfoRow label="Type" value={tc.label} />
                        {event.location && <InfoRow label="Lieu" value={event.location} icon={MapPin} />}
                        {event.assigned_to_name && <InfoRow label="Assigné à" value={event.assigned_to_name} icon={User} />}
                        {event.description && <InfoRow label="Description" value={event.description} icon={FileText} />}
                    </AccordionSection>

                    {/* Prospect sections */}
                    {p && (
                        <>
                            <div ref={(el) => { sectionRefs.current.source = el; }}>
                            <AccordionSection title="Source" isOpen={openSections.source} onToggle={() => toggle('source')} completed={!!(p.source && p.departement)} highlight={incompleteSectionIds.includes('source')}>
                                <InfoRow label="Source du prospect" value={getSourceLabel(p.source)} icon={Target} />
                                <InfoRow label="Département" value={getDeptLabel(p.departement)} icon={Building} />
                            </AccordionSection>
                            </div>

                            <div ref={(el) => { sectionRefs.current.contact = el; }}>
                            <AccordionSection title="Contact" isOpen={openSections.contact} onToggle={() => toggle('contact')} completed={!!(p.nom && (p.telephone || p.email))} highlight={incompleteSectionIds.includes('contact')}>
                                <InfoRow label="Nom" value={p.nom} icon={User} />
                                <InfoRow label="Téléphone" value={p.telephone} icon={Phone} href={p.telephone ? `tel:${p.telephone}` : undefined} />
                                <InfoRow label="Email" value={p.email} icon={Mail} href={p.email ? `mailto:${p.email}` : undefined} />
                            </AccordionSection>
                            </div>

                            <div ref={(el) => { sectionRefs.current.depart = el; }}>
                            <AccordionSection title="Adresse de départ" isOpen={openSections.depart} onToggle={() => toggle('depart')} completed={!!p.adresse_depart?.adresse} highlight={incompleteSectionIds.includes('depart')}>
                                {p.adresse_depart ? (
                                    <>
                                        <InfoRow label="Date chargement" value={fmtDateShort(p.adresse_depart.date)} icon={CalendarIcon} />
                                        <InfoRow label="Adresse" value={p.adresse_depart.adresse} icon={MapPin} />
                                        <InfoRow label="Étage" value={p.adresse_depart.etage} />
                                        <InfoRow label="Ascenseur" value={getAscLabel(p.adresse_depart.ascenseur)} />
                                        <InfoRow label="Portage" value={p.adresse_depart.portage} />
                                        <InfoRow label="Autorisation stationnement" value={p.adresse_depart.autorisation_stationnement ? 'Oui' : 'Non'} />
                                        <InfoRow label="Monte meuble" value={p.adresse_depart.monte_meuble ? 'Oui' : 'Non'} />
                                    </>
                                ) : (
                                    <div className="p-6 text-center text-gray-500">Aucune adresse de départ renseignée</div>
                                )}
                            </AccordionSection>
                            </div>

                            <div ref={(el) => { sectionRefs.current.arrivee = el; }}>
                            <AccordionSection title="Adresse d'arrivée" isOpen={openSections.arrivee} onToggle={() => toggle('arrivee')} completed={!!p.adresse_arrivee?.adresse} highlight={incompleteSectionIds.includes('arrivee')}>
                                {p.adresse_arrivee ? (
                                    <>
                                        <InfoRow label="Date déchargement" value={fmtDateShort(p.adresse_arrivee.date)} icon={CalendarIcon} />
                                        <InfoRow label="Adresse" value={p.adresse_arrivee.adresse} icon={MapPin} />
                                        <InfoRow label="Étage" value={p.adresse_arrivee.etage} />
                                        <InfoRow label="Ascenseur" value={getAscLabel(p.adresse_arrivee.ascenseur)} />
                                        <InfoRow label="Portage" value={p.adresse_arrivee.portage} />
                                        <InfoRow label="Autorisation stationnement" value={p.adresse_arrivee.autorisation_stationnement ? 'Oui' : 'Non'} />
                                        <InfoRow label="Monte meuble" value={p.adresse_arrivee.monte_meuble ? 'Oui' : 'Non'} />
                                    </>
                                ) : (
                                    <div className="p-6 text-center text-gray-500">Aucune adresse d'arrivée renseignée</div>
                                )}
                            </AccordionSection>
                            </div>

                            {/* Ghost anchors for highlight + scroll on prospect-only sections (Formule, Garde meuble, Calcul tarif, Inventaire, Fournitures, Calcul des coûts) */}
                            {['formule', 'garde_meuble', 'calcul_tarif', 'inventaire', 'fournitures', 'calcul_couts'].filter(sid => incompleteSectionIds.includes(sid)).map(sid => {
                                const titleMap = {
                                    formule: 'Formule', garde_meuble: 'Garde meuble',
                                    calcul_tarif: 'Calcul tarif', inventaire: 'Inventaire / Volume',
                                    fournitures: 'Fournitures', calcul_couts: 'Calcul des coûts',
                                };
                                return (
                                    <div key={sid} ref={(el) => { sectionRefs.current[sid] = el; }}
                                         className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 cursor-pointer hover:bg-red-100/70 transition-colors"
                                         onClick={goEditProspect}
                                         data-testid={`incomplete-anchor-${sid}`}>
                                        <div className="w-1.5 h-8 rounded-full bg-red-500" />
                                        <div className="flex-1 text-left">
                                            <h3 className="text-sm font-semibold text-red-700">{titleMap[sid]}</h3>
                                            <p className="text-xs text-red-600/80 mt-0.5">Incomplet — cliquez pour modifier le prospect</p>
                                        </div>
                                        <Pencil className="w-4 h-4 text-red-600" />
                                    </div>
                                );
                            })}

                            <AccordionSection title="Remarque prospect" isOpen={openSections.remarques} onToggle={() => toggle('remarques')} completed={!!p.remarques}>
                                <div className="p-6">
                                    {p.remarques ? (
                                        <p className="text-[#1B365D] whitespace-pre-wrap">{p.remarques}</p>
                                    ) : (
                                        <p className="text-gray-500 text-center">Aucune remarque</p>
                                    )}
                                </div>
                            </AccordionSection>
                        </>
                    )}

                    {/* If no prospect linked, show description */}
                    {!p && event.description && (
                        <AccordionSection title="Description" isOpen={openSections.remarques} onToggle={() => toggle('remarques')} completed>
                            <div className="p-6">
                                <p className="text-[#1B365D] whitespace-pre-wrap">{event.description}</p>
                            </div>
                        </AccordionSection>
                    )}
                </div>
            </div>

            {/* ============ EDIT MODAL - Accordion style like ProspectForm ============ */}
            <Dialog open={showEditModal} onOpenChange={(open) => {
                if (!open) { confirmNavigation(() => { setShowEditModal(false); setHasChanges(false); }); }
            }}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
                    <DialogHeader className="p-6 pb-0">
                        <DialogTitle className="text-[#1B365D] text-xl flex items-center gap-2">
                            <Pencil className="w-5 h-5 text-[#C9A962]" />
                            Modifier l'événement
                        </DialogTitle>
                        <DialogDescription>{editForm.date && fmtDate(editForm.date)}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEdit}>
                        <div className="p-6 space-y-4">
                            {/* Rendez-vous section */}
                            <AccordionSection title="Rendez-vous" isOpen={editSections.rdv} onToggle={() => setEditSections(s => ({ ...s, rdv: !s.rdv }))} completed={!!editForm.title}>
                                <FormRow label="Titre" icon={FileText}>
                                    <Input value={editForm.title || ''} onChange={(e) => updateEditForm({ title: e.target.value })} placeholder="Titre de l'événement" className="bg-gray-50 border-gray-200" data-testid="edit-title-input" />
                                </FormRow>
                                <FormRow label="Type" icon={Layers}>
                                    <Select value={editForm.type} onValueChange={(v) => updateEditForm({ type: v })}>
                                        <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {EVENT_TYPES.map(t => (
                                                <SelectItem key={t.value} value={t.value}>
                                                    <div className="flex items-center gap-2"><div className={`w-3 h-3 rounded ${t.color}`} />{t.label}</div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormRow>
                                <FormRow label="Date" icon={CalendarIcon}>
                                    <Input type="date" value={editForm.date || ''} onChange={(e) => updateEditForm({ date: e.target.value })} className="bg-gray-50 border-gray-200" />
                                </FormRow>
                                <FormRow label="Heure début" icon={Clock}>
                                    <Select value={editForm.start_time} onValueChange={(v) => updateEditForm({ start_time: v })}>
                                        <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                                        <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </FormRow>
                                <FormRow label="Heure fin" icon={Clock}>
                                    <Select value={editForm.end_time} onValueChange={(v) => updateEditForm({ end_time: v })}>
                                        <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                                        <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </FormRow>
                                <FormRow label="Lieu" icon={MapPin}>
                                    <Input value={editForm.location || ''} onChange={(e) => updateEditForm({ location: e.target.value })} placeholder="Adresse ou lieu" className="bg-gray-50 border-gray-200" />
                                </FormRow>
                                <FormRow label="Description" icon={FileText}>
                                    <Textarea value={editForm.description || ''} onChange={(e) => updateEditForm({ description: e.target.value })} placeholder="Description" rows={3} className="bg-gray-50 border-gray-200" />
                                </FormRow>
                            </AccordionSection>

                            {/* Prospect section - Read-only */}
                            {editForm.prospect_id && (
                            <AccordionSection title="Prospect lié" isOpen={editSections.prospect !== false} onToggle={() => setEditSections(s => ({ ...s, prospect: s.prospect === false ? true : false }))} completed={!!editForm.prospect_id}>
                                <FormRow label="Prospect" icon={User}>
                                    <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-[#1B365D] font-medium" data-testid="edit-prospect-name">
                                        {event?.prospect?.nom || 'Prospect inconnu'}
                                        {event?.prospect?.telephone && <span className="text-gray-400 ml-2">- {event.prospect.telephone}</span>}
                                    </div>
                                </FormRow>
                            </AccordionSection>
                            )}

                            {/* Reschedule History */}
                            {event?.reschedule_history?.length > 0 && (
                            <AccordionSection title={`Historique reports (${event.reschedule_history.length})`} isOpen={true} onToggle={() => {}}>
                                <div className="space-y-2">
                                    {event.reschedule_history.map((h, i) => (
                                        <div key={i} className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                                            <History className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                                            <div>
                                                <p className="text-amber-800 font-medium">
                                                    Reporté du {h.old_date} ({h.old_start_time}-{h.old_end_time}) au {h.new_date} ({h.new_start_time}-{h.new_end_time})
                                                </p>
                                                <p className="text-amber-600 text-xs mt-0.5">
                                                    Par {h.changed_by} le {h.changed_at ? format(parseISO(h.changed_at), 'dd/MM/yyyy à HH:mm', { locale: fr }) : ''}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </AccordionSection>
                            )}

                            {/* Attribution section */}
                            {canAssign && (
                                <AccordionSection title="Attribution" isOpen={editSections.assignment} onToggle={() => setEditSections(s => ({ ...s, assignment: !s.assignment }))} completed={!!editForm.assigned_to}>
                                    <FormRow label="Assigné à" icon={User}>
                                        <Select value={editForm.assigned_to || '_none'} onValueChange={(v) => updateEditForm({ assigned_to: v === '_none' ? '' : v })}>
                                            <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="_none">— Non assigné —</SelectItem>
                                                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </FormRow>
                                </AccordionSection>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-3 p-6 pt-0">
                            <Button type="button" variant="outline" onClick={() => confirmNavigation(() => { setShowEditModal(false); setHasChanges(false); })}>Annuler</Button>
                            <Button type="submit" disabled={submitting} className="bg-[#1B365D] hover:bg-[#2A4A7A] gap-2" data-testid="edit-submit-button">
                                <Save className="w-4 h-4" />
                                {submitting ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Supprimer l'événement</DialogTitle>
                        <DialogDescription>Êtes-vous sûr de vouloir supprimer cet événement ? Cette action est irréversible.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Annuler</Button>
                        <Button onClick={handleDelete} disabled={submitting} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-button">
                            {submitting ? 'Suppression...' : 'Supprimer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ============ VISITE TERMINEE VALIDATION DIALOG ============ */}
            <Dialog open={incompleteDialog.open} onOpenChange={(open) => { if (!open) setIncompleteDialog({ open: false, sections: [] }); }}>
                <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="incomplete-sections-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <Target className="w-5 h-5 text-red-500" />
                            Informations incomplètes
                        </DialogTitle>
                        <DialogDescription>
                            Pour passer la visite en <strong>Terminée</strong>, tous les champs ci-dessous doivent être complétés.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-red-50 border border-red-100 rounded-lg p-4 space-y-3 text-sm" data-testid="incomplete-sections-list">
                        {incompleteDialog.sections.map((s) => (
                            <div key={s.id}>
                                <div className="flex items-center gap-2 font-semibold text-red-700 mb-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                    {s.title}
                                </div>
                                <ul className="ml-5 space-y-0.5">
                                    {(s.fields || []).map((f, i) => (
                                        <li key={i} className="text-red-600/90 text-[13px] flex items-center gap-1.5">
                                            <span className="text-red-400">—</span> {f}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
                        <Button type="button" variant="outline" onClick={() => setIncompleteDialog({ open: false, sections: [] })} data-testid="incomplete-ok-btn">
                            OK
                        </Button>
                        <Button
                            type="button"
                            onClick={scrollToFirstIncomplete}
                            variant="outline"
                            className="border-[#1B365D]/30 text-[#1B365D] hover:bg-[#1B365D]/5 gap-1.5"
                            data-testid="incomplete-scroll-btn"
                        >
                            <ChevronDown className="w-4 h-4" />
                            Voir la section
                        </Button>
                        <Button
                            type="button"
                            onClick={goEditProspect}
                            className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-1.5"
                            data-testid="incomplete-edit-btn"
                        >
                            <Pencil className="w-4 h-4" />
                            Modifier le prospect
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel-reason dialog */}
            <Dialog open={cancelDialog.open} onOpenChange={(o) => !cancelDialog.submitting && setCancelDialog({ open: o, reason: o ? cancelDialog.reason : '', submitting: false })}>
                <DialogContent className="sm:max-w-md" data-testid="cancel-reason-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D]">Annuler la visite</DialogTitle>
                        <DialogDescription>
                            Indiquez la raison de l'annulation. Elle sera conservée sur le rendez-vous et la fiche du prospect.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2 space-y-2">
                        <Label htmlFor="cancel-reason-input" className="text-sm">Raison <span className="text-red-500">*</span></Label>
                        <Textarea
                            id="cancel-reason-input"
                            value={cancelDialog.reason}
                            onChange={(e) => setCancelDialog(d => ({ ...d, reason: e.target.value }))}
                            placeholder="Ex: client injoignable, décalage de calendrier, devis refusé..."
                            rows={4}
                            disabled={cancelDialog.submitting}
                            data-testid="cancel-reason-input"
                        />
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCancelDialog({ open: false, reason: '', submitting: false })}
                            disabled={cancelDialog.submitting}
                            data-testid="cancel-reason-cancel-btn"
                        >
                            Retour
                        </Button>
                        <Button
                            type="button"
                            onClick={submitCancellation}
                            disabled={cancelDialog.submitting || !cancelDialog.reason.trim()}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="cancel-reason-confirm-btn"
                        >
                            {cancelDialog.submitting ? 'Annulation...' : "Confirmer l'annulation"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <UnsavedDialog />
        </Layout>
    );
}
